import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { classNames } from '@/lib/utils';
import Modal from '@/components/ui/Modal';
import { IDIOMAS } from '@/components/copy/tipos';
import { Lightbulb, Loader2, Sparkles } from 'lucide-react';

interface Props {
  abierto: boolean;
  anuncioId: number;
  idioma: string;
  onCerrar: () => void;
  onElegir: (titulo: string) => void;
}

/** Límite del título del anuncio en Hostify; se avisa, no se recorta. */
const MAX_TITULO = 50;

function nombreIdioma(codigo: string): string {
  return IDIOMAS.find((i) => i.code === codigo)?.label ?? codigo.toUpperCase();
}

export default function ModalTitulos({
  abierto,
  anuncioId,
  idioma,
  onCerrar,
  onElegir,
}: Props) {
  const [pistas, setPistas] = useState('');
  const [titulos, setTitulos] = useState<string[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;
    setPistas('');
    setTitulos([]);
    setError(null);
    setCargando(false);
  }, [abierto]);

  async function generar() {
    setCargando(true);
    setError(null);
    try {
      const res = await api.generateCopyTitles(anuncioId, {
        language: idioma,
        suggestions: pistas.trim() || undefined,
      });
      setTitulos(res.titles || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron generar los títulos');
    } finally {
      setCargando(false);
    }
  }

  const elegir = (titulo: string) => {
    onElegir(titulo);
    onCerrar();
  };

  return (
    <Modal isOpen={abierto} onClose={onCerrar} title="Idear título con IA" size="lg">
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <Lightbulb size={20} className="text-sereno-dark/40 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-sereno-dark/70">
            La IA mira las fotos, la ubicación, la descripción y el equipamiento del anuncio
            para proponer <strong>5 títulos</strong> en{' '}
            <strong>{nombreIdioma(idioma)}</strong>.
          </p>
        </div>

        <div>
          <label className="label" htmlFor="copy-titulos-pistas">
            Pistas para la IA <span className="text-sereno-dark/40">(opcional)</span>
          </label>
          <textarea
            id="copy-titulos-pistas"
            className="input"
            rows={2}
            value={pistas}
            onChange={(e) => setPistas(e.target.value)}
            placeholder="Ej: que mencione las vistas al mar, estilo moderno, piscina privada…"
          />
        </div>

        {error && <p className="text-sm text-rose-600">{error}</p>}

        {cargando && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-sereno-dark/50">
            <Loader2 size={18} className="animate-spin" />
            Analizando fotos y contexto…
          </div>
        )}

        {!cargando && titulos.length > 0 && (
          <div className="space-y-2">
            <p className="label">Propuestas</p>
            {titulos.map((titulo, i) => (
              <button
                // Dos propuestas pueden salir idénticas, así que la posición es
                // la única clave estable dentro de una misma tanda.
                key={`${i}-${titulo}`}
                type="button"
                onClick={() => elegir(titulo)}
                className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors flex items-start gap-3"
              >
                <Sparkles size={16} className="text-sereno-dark/40 flex-shrink-0 mt-0.5" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-sereno-dark break-words">{titulo}</span>
                  <span
                    className={classNames(
                      'block text-xs mt-1 tabular-nums',
                      titulo.length > MAX_TITULO ? 'text-rose-600' : 'text-sereno-dark/40',
                    )}
                  >
                    {titulo.length}/{MAX_TITULO} caracteres
                    {titulo.length > MAX_TITULO && ' — se pasa del máximo'}
                  </span>
                </span>
              </button>
            ))}
            <p className="text-xs text-sereno-dark/50 pt-1">
              Al elegir un título se escribe en el campo <strong>Título</strong> del editor,
              pero <strong>no se guarda</strong>: hay que pulsar Guardar para que llegue a
              Hostify.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onCerrar}>
            Cerrar
          </button>
          <button
            type="button"
            className="btn-primary flex items-center gap-2"
            disabled={cargando}
            onClick={() => { void generar(); }}
          >
            {cargando ? <Loader2 size={16} className="animate-spin" /> : <Lightbulb size={16} />}
            {titulos.length > 0 ? 'Generar otros 5' : 'Generar títulos'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
